import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

/**
 * Searchable single-select combobox.
 * Renders its dropdown INLINE (no portal) so it scrolls correctly even inside a
 * Radix Dialog (whose scroll-lock would otherwise block a portaled popover).
 * options: [{ value, label }]
 */
export function Combobox({
  options = [], value, onChange, placeholder = 'Select…', searchPlaceholder = 'Search…',
  emptyText = 'No results.', disabled = false, className, triggerClassName, testid,
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = options.find((o) => o.value === value);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={cn('w-full justify-between font-normal bg-card', !selected && 'text-muted-foreground', triggerClassName)}
      >
        <span className="truncate">{selected ? (selected.triggerLabel || selected.label) : placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className={cn('absolute z-50 mt-1 w-full min-w-[220px] rounded-md border border-border bg-popover shadow-lg', className)}>
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="h-9" autoFocus />
            <CommandList className="max-h-[240px] overflow-y-auto overscroll-contain">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                    data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  >
                    <span className="truncate flex-1">{o.label}</span>
                    <Check className={cn('ml-2 h-4 w-4 shrink-0', value === o.value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

export default Combobox;
